<template>
	<v-dialog v-model="dialog" persistent max-width="600px">
		<v-card>
			<v-toolbar flat dense>
				<v-toolbar-title class="subheading">Policy Settings</v-toolbar-title>
			</v-toolbar>

			<v-card-text>
				<v-combobox
					ref="categoryInput"
					v-model="form.categoryInput"
					:items="hrDocumentCategories"
					item-text="name"
					item-value="id"
					label="Select or type a policy category"
					clearable
					persistent-hint
					hint="Use categories to help group your policies"
					class="mt-2 pt-0"
				>
					<template slot="item" slot-scope="data">
						<v-list-tile @click="selectCategory(data.item)">
							<v-list-tile-content>
								<v-list-tile-title>{{ data.item.name }}</v-list-tile-title>
							</v-list-tile-content>
							<v-list-tile-action v-if="canDeleteCategory(data.item)">
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-icon small  class="mr-2" v-on="on" @click.stop="deleteCategory(data.item)">
											delete
										</v-icon>
									</template>
									<span>Delete Category</span>
								</v-tooltip>
							</v-list-tile-action>
						</v-list-tile>
					</template>
				</v-combobox>

				<v-divider class="my-4"></v-divider>

				<v-switch class="mb-4" v-model="form.mandatoryForAll" :label="`Mandatory for all ${$term('employee', { plural: true })}`" persistent-hint :hint="`Determines whether all ${$term('employee', { plural: true })} are required to acknowledge to this policy.`"></v-switch>

				<!-- <v-divider class="my-4"></v-divider> -->

				<v-subheader class="text-xs-left pl-0">Acknowledgement Text</v-subheader>
				<v-radio-group class="mt-0 ack-text-group" v-model="form.acknowledgementTextOption" :mandatory="true">
					<v-radio label="I acknowledge, agree and understand all aspects of this policy." :value="0"></v-radio>
					<v-radio class="mt-2" label="I acknowledge, agree and understand all aspects of this policy. I am required to comply with this policy and there may be disciplinary consequences if I fail to comply." :value="1"></v-radio>
				</v-radio-group>
			</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn flat @click="onCancelClick">Cancel</v-btn>
				<v-btn flat color="primary" :loading="isSaving" :disabled="isSaving" @click="onSaveClick">
					Save
					<span slot="loader">Saving...</span>
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
	import avatarMixin from '@/mixins/avatarMixin';
	import { mapGetters } from 'vuex';

	export default {
		props: {
			dialog: {
				required: true
			},
			relevantParties: {
				type: Object,
				required: true
			},
			isSaving: {
				type: Boolean,
				required: false
			},
			acknowledgementTextOption: {
				type: Number,
				required: true
			},
			currentCategory: {
				type: Object,
				required: false
			}
		},
		data() {
			return {
				form: null,
				headers: [
					{ text: this.$term('employee', { case: 'title' }), value: 'name' },
					{ text: 'Acknowledged at', value: 'acknowledged', sortable: false }
				]
			};
		},
		created() {
			this.form = {
				mandatoryForAll: this.relevantParties.all,
				acknowledgementTextOption: this.acknowledgementTextOption,
				categoryInput: this.currentCategory ? this.currentCategory : null
			};
		},
		mixins: [avatarMixin],
		computed: {
			...mapGetters({
				hrDocumentCategories: 'hrDocuments/hrDocumentCategories'
			})
		},
		methods: {
			selectCategory(item) {
				this.form.categoryInput = item;
				this.$refs.categoryInput.blur();
			},
			onCancelClick() {
				this.$emit('onCancel');
			},
			canDeleteCategory(category) {
				// Only allow delete if it's not referenced in any documents
				return category.references == null || category.references.length === 0;
			},
			deleteCategory(category) {
				if (confirm(`Are you sure you want to remove the category "${category.name}"?`)) {
					this.$store.dispatch('hrDocuments/deleteCategory', category.id);
				}
			},
			onSaveClick() {
				// Force blur on combobox to flush any pending input
				this.$refs.categoryInput.blur();

				this.$nextTick(() => {
					const input = this.form.categoryInput;
					let resolvedCategory = null;

					if (input && typeof input === 'object') {
						// Existing selection
						resolvedCategory = {
							id: input.id,
							name: input.name
						};
					} else if (typeof input === 'string' && input.trim() !== '') {
						// New entry typed by user
						resolvedCategory = {
							id: null,
							name: input.trim()
						};
					}

					this.$emit('onSave', {
						mandatoryForAll: this.form.mandatoryForAll,
						acknowledgementTextOption: this.form.acknowledgementTextOption,
						category: resolvedCategory
					});
				});
			}
		}
	};
</script>

<style scoped>
	.ack-text-group >>> label {
		font-size: 14px;
	}
</style>
